<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">{{name}}</h1>

                    <a v-if="!isDeleted" @click="handleClearDatasetItemsCommandV1" role="button" class="btn btn-link btn-sm">
                        <i class="fa fa-eraser" aria-hidden="true"></i>
                        <span>Clear dataset</span>
                    </a>

                    <a v-if="!isDeleted" @click="handleDeleteDatasetCommandV1" role="button" class="btn btn-link btn-sm">
                        <i class="fa-regular fa-trash-can"></i>
                        <span>Delete</span>
                    </a>

                    <a v-else @click="handlePermanentDeleteDatasetCommandV1" role="button" class="btn btn-link btn-sm">
                        <i class="fa fa-remove" aria-hidden="true"></i>
                        <span>Permanently delete</span>
                    </a>
                </div>
                <div class="col-lg-5 col-sm-6 text-right">
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div class="container">
            <div class="border-box mb-3 p-4">
                <div class="row mb-0">
                    <div class="col-11">
                        <h5 class="m-0">REST API</h5>
                    </div>
                    <div class="col-1">
                        <a v-show="!showApi" role="button" @click="showApi=true"><i class="fa-solid fa-chevron-down"></i></a>
                        <a v-show="showApi" role="button" @click="showApi=false"><i class="fa-solid fa-chevron-up"></i></a>
                    </div>
                </div>

                <div v-show="showApi">
                    <br />
                    <strong>Authorization: API Key (AccessToken = YOUR_ACCESS_TOKEN)</strong>
                    <a href="/#/organizations/accesstokens/my" target="_blank">My Access Tokens</a>
                    <hr />
                    <p>
                        <strong>Get all items in dataset</strong><br />
                        GET /integrations/api/datasets/2024-10-24/{{id}}/data
                    </p>
                    <p>
                        <strong>Get signle item from dataset</strong><br />
                        GET /integrations/api/datasets/2024-10-24/{{id}}/data/{ID_PROPERTY_NAME}/{ID}
                    </p>
                    <p>
                        <strong>Add item to dataset</strong><br />
                        POST /integrations/api/datasets/2024-10-24/{{id}}/data/{ID_PROPERTY_NAME}/{ID}
<pre>
{
    "Name": "My Project 123",
    "Description": "My project description"
}
</pre>
                    </p>
                    <p>
                        <strong>Update item in dataset</strong><br />
                        PUT /integrations/api/datasets/2024-10-24/{{id}}/data/{ID_PROPERTY_NAME}/{ID}
<pre>
{
    "Description": "My project NEW description"
}
</pre>
                    </p>
                    <p>
                        <strong>Delete item from dataset</strong><br />
                        DELETE /integrations/api/datasets/2024-10-24/{{id}}/data/{ID_PROPERTY_NAME}/{ID}
                    </p>
                </div>
            </div>

            <strong>Items: {{data.length}}</strong>

            <json-viewer if="data" :value="data" :expand-depth=1 expanded copyable boxed sort></json-viewer>

            <ul>
                <li v-for="c in changesReversed">
                    {{c.Details}} <a :href="`/#/admin/integrations/runs/details/${tid}/${c.RunId}`">Run</a>
                </li>
            </ul>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, onMounted, watch } from 'vue'
    import { useId, useRouter } from '@common/composables'
    import { useAdminDatasetsDetailsPage } from '@Integrations/Datasets/pages-core'

    const router = useRouter()
    const id = useId()

    const {
        useCurrentUserQuery,
        useDatasetDetailsQuery,
        useDatasetChangeLogQueryV1,
        useClearDatasetItemsCommand,
        useDeleteDatasetCommand,
        usePermanentDeleteDatasetCommand,
    } = useAdminDatasetsDetailsPage()

    const showApi = ref(false)

    const { modelCurrentUserQueryV1, runCurrentUserQueryV1 } = useCurrentUserQuery()
    const userId = computed(() => modelCurrentUserQueryV1.value?.UserId)

    const { modelDatasetDetailsQueryV1, runDatasetDetailsQueryV1 } = useDatasetDetailsQuery(id)
    const tid = computed(() => modelDatasetDetailsQueryV1.value?.OrganizationUnitId)
    const name = computed(() => modelDatasetDetailsQueryV1.value?.Name)
    const data = computed(() => modelDatasetDetailsQueryV1.value?.Data)
    const isDeleted = computed(() => modelDatasetDetailsQueryV1.value?.IsDeleted)

    const { modelDatasetChangeLogQueryV1, runDatasetChangeLogQueryV1 } = useDatasetChangeLogQueryV1(id)
    const changes = computed(() => modelDatasetChangeLogQueryV1.value?.Changes || [])
    const changesReversed = computed(() => changes.value.reverse())

    const { invokeClearDatasetItemsCommandV1, succeedClearDatasetItemsCommandV1 } = useClearDatasetItemsCommand(id, tid, userId)

    const { invokeDeleteDatasetCommandV1, succeedDeleteDatasetCommandV1 } = useDeleteDatasetCommand(id, tid, userId)
    const { invokePermanentDeleteDatasetCommandV1, succeedPermanentDeleteDatasetCommandV1 } = usePermanentDeleteDatasetCommand(id)

    function handleClearDatasetItemsCommandV1() {
        if (confirm('Do you want to clear all dataset items permenently?')) {
            invokeClearDatasetItemsCommandV1()
        }
    }

    function handleDeleteDatasetCommandV1() {
        if (confirm(`Do you really want to delete dataset ${name.value}?`)) invokeDeleteDatasetCommandV1()
    }

    function handlePermanentDeleteDatasetCommandV1() {
        if (confirm(`Do you really want to PERMANENTLY delete dataset ${name.value}?`)) invokePermanentDeleteDatasetCommandV1()
    }

    onMounted(() => {
        runCurrentUserQueryV1()
        runDatasetDetailsQueryV1()
        runDatasetChangeLogQueryV1()
    })

    watch(name, () => document.title = name.value)

    watch([succeedClearDatasetItemsCommandV1, succeedDeleteDatasetCommandV1], () => runDatasetDetailsQueryV1())

    watch(succeedPermanentDeleteDatasetCommandV1, (val) => val ? router.push(`/admin/integrations/datasets/manage/${tid.value}`) : null)
</script>
